import { useCallback, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'

import { AgentRunRow } from '@/components/px/AgentRunRow'
import { AppShell } from '@/components/px/AppShell'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { asApiError } from '@/services/apiError'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { PhaseRibbon } from '@/components/px/PhaseRibbon'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { projectNav } from '@/components/px/nav'
import {
  AGENT_PANEL_TITLE,
  OVERNIGHT_RUNS,
  RUN_LOG_FOOTNOTE,
  RUN_LOG_LINK,
} from '@/demo/content/projectOverview'
import { agentById } from '@/demo/data'
import { selectActiveOrgId, selectSessionPrincipalKey } from '@/features/auth/slices/sessionSlice'
import { useCoalescedRealtimeReload } from '@/features/realtime/hooks/useCoalescedRealtimeReload'
import { ProjectBlockedState } from '@/features/projects/components/ProjectBlockedState'
import { ProjectMembersPanel } from '@/features/projects/components/ProjectMembersPanel'
import { epochLabel, healthTone, isoDayLabel, toRibbonPhase } from '@/features/projects/format'
import {
  clearProjectDetailError,
  openProject,
  closeProject,
  selectProjectBlocked,
  selectProjectDetail,
  selectProjectDetailError,
  selectProjectDetailLoading,
  selectProjectMembers,
} from '@/features/projects/slices/projectDetailSlice'
import { GatesPanel } from '@/features/phase-gates/components/GatesPanel'
import {
  gatesClosed,
  openGates,
  selectGatesError,
  selectGatesNonce,
  selectPhaseView,
  startEngagement,
} from '@/features/phase-gates/slices/phaseGatesSlice'
import { ProjectActivityFeed } from '@/features/activity-feed/components/ProjectActivityFeed'
import { ProjectDocumentsPanel } from '@/features/project-files/components/ProjectDocumentsPanel'
import { VersionsPanel } from '@/features/versions/components/VersionsPanel'
import type { BoardTaskRow } from '@/features/board/types'
import {
  openBoard,
  selectBoardBlocked,
  selectBoardListError,
  selectBoardLoading,
  selectBoardNonce,
  selectBoardProgress,
  selectTasksAwaitingHuman,
} from '@/features/board/slices/boardSlice'
import {
  openRequirements,
  selectFitGapSummary,
  selectRequirementsBlocked,
  selectRequirementsError,
  selectRequirementsLoading,
  selectRequirementsNonce,
} from '@/features/requirements/slices/requirementsSlice'
import {
  selectActiveBudgetMissing,
  selectBudgetVsActual,
  selectBudgetsError,
  selectBudgetsLoading,
} from '@/features/budgets/selectors'
import { fetchActiveBudget, fetchBudgetVsActual } from '@/features/budgets/slices/budgetsSlice'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'
import { projectOverviewRail } from './ProjectOverviewRail'

/**
 * SURFACE 3.1 — PROJECT OVERVIEW (`/project/:projectId`), FURTHER RETIRED
 * (PX-W6.3 → PX-W7.6, TASK-FD4686D1):
 *
 * the engagement workspace. Phase + crosswalk + ageing render from the
 * server's `GET …/phase` projection VERBATIM (no client phase math), the
 * four gates carry approve/return, the roster / work packages / documents
 * and the live activity feed are real, and a non-member sees the server's
 * keyed 403/404 as the blocked state — never an empty workspace, never demo
 * content (AC-2/4/5/7 of PX-W6.3).
 *
 * PX-W7.6 moved the KPI row, the needs-a-human table and the team roster
 * ABOVE the badge, onto the requirements slice (PX-W7.2), the board slice
 * (PX-W7.4), the already-live budgets slice and the live members
 * projection. The badged `<section data-testid="demo-band">` now holds only
 * the overnight-runs panel — the one element with no list endpoint yet (a
 * change request on TASK-E342142F). This page issues NO request of its own:
 * every figure rides a slice another feature already loads.
 */
function Panel({
  title,
  className,
  children,
}: {
  title: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <section
      aria-label={title}
      className={cn('rounded-lg border border-border bg-card px-4 py-3.5', className)}
    >
      <SectionLabel>{title}</SectionLabel>
      <div className="mt-1.5">{children}</div>
    </section>
  )
}

/**
 * REVIEW D1 — the PRE-KICKOFF card. Rendered IN PLACE of the phase ribbon
 * while the loaded phase view has no `currentPhase`: `PhaseRibbon` (locked
 * furniture) throws on an unknown phase via `demo/architecture`'s
 * `engagementPhaseFor`, so a bare cast of '' unmounts the whole tree and the
 * Start-engagement control — the only UI path to onboard a new project —
 * dies with it. The card keeps that control PROMINENT instead.
 */
function KickoffPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const error = useAppSelector(selectGatesError)

  // Phase-transition refusals carry no gateRef; decision refusals do.
  const startError = error !== null && error.gateRef === undefined ? error : null

  return (
    <section
      aria-label={t('phase.startEngagement')}
      className="mt-5 rounded-lg border-2 border-primary bg-card px-5 py-4"
      data-testid="kickoff-panel"
    >
      <SectionLabel>{t('phase.panelTitle')}</SectionLabel>
      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-sm text-ink-soft">{t('phase.noPhase')}</p>
        <button
          type="button"
          onClick={() => void dispatch(startEngagement({ orgId, projectId }))}
          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          data-testid="start-engagement"
        >
          {t('phase.startEngagement')}
        </button>
      </div>
      {startError !== null && (
        <p role="alert" className="mt-2 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground">
          {startError.message}
          {startError.code !== undefined && (
            <span className="ml-1.5 font-mono text-xs text-muted-foreground">{startError.code}</span>
          )}
        </p>
      )}
    </section>
  )
}

/** The phase panel — server projection, crosswalk verbatim, ageing as a number. */
function PhasePanel() {
  const { t } = useTranslation('projectWorkspace')
  const phase = useAppSelector(selectPhaseView)

  const crosswalk = phase?.crosswalk

  return (
    <section aria-label={t('phase.panelTitle')} className="rounded-lg border border-border bg-card px-4 py-3.5">
      <SectionLabel>{t('phase.panelTitle')}</SectionLabel>
      <div className="mt-1.5 grid gap-3 lg:grid-cols-3">
          <dl className="lg:col-span-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">{t('phase.currentPhase')}</dt>
              <dd className="mt-0.5 font-medium text-foreground">{crosswalk?.name ?? phase?.currentPhase}</dd>
              {crosswalk?.subLabel !== undefined && (
                <dd className="text-xs text-muted-foreground">{crosswalk.subLabel}</dd>
              )}
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">{t('phase.ageLabel')}</dt>
              <dd className="v-figure mt-0.5 font-serif text-[22px] font-semibold tabular-nums text-foreground">
                <span title={t('phase.ageDays', { count: phase?.phaseAgeDays ?? 0 })}>
                  {phase?.phaseAgeDays ?? 0}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">{t('phase.enteredAt')}</dt>
              <dd className="mt-0.5 text-foreground">{epochLabel(phase?.enteredAt)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">{t('phase.plannedEnd')}</dt>
              <dd className="mt-0.5 text-foreground">{epochLabel(phase?.plannedEnd)}</dd>
            </div>
          </dl>
          {crosswalk !== undefined && (
            <dl className="rounded-md border border-border px-3 py-2 text-sm">
              <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">{t('phase.crosswalkTitle')}</dt>
              <dd className="mt-1 space-y-0.5">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">{t('phase.revAPhase')}</span>
                  <span className="font-medium text-foreground">{crosswalk.revAPhase ?? '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">{t('phase.flowStep')}</span>
                  <span className="font-medium text-foreground">{crosswalk.flowStep ?? '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">{t('phase.gateLabel')}</span>
                  <span className="font-medium text-foreground">{crosswalk.gateLabel ?? '—'}</span>
                </div>
                {crosswalk.mappingNote !== undefined && (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="crosswalk-mapping-note">
                    {t('phase.mappingNote')}: {crosswalk.mappingNote}
                  </p>
                )}
              </dd>
            </dl>
          )}
        </div>
    </section>
  )
}

/** €25 900 000 (minor units) → "€259k" — the overview's own compact currency shorthand. */
const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' }
const minorToK = (minor: number, currency: string): string => {
  const symbol = CURRENCY_SYMBOLS[currency] ?? `${currency} `
  return `${symbol}${Math.round(minor / 100_000)}k`
}

/**
 * The degrade treatment the three live KPI cards share: `—` once that card's
 * own read has failed or been refused, `···` while it is still in flight — so
 * one card degrades alone and the row never blanks (AC-11). Deliberately NOT
 * `<ErrorPanel>`/`<LoadingPanel>`: those are block-level and would break the
 * compact KPI grid. Each call site passes its own literal test id, so a grep
 * for `kpi-build-failed` still lands on the card that renders it.
 */
function KpiFailed({ label, testId }: { label: string; testId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const figure = <span data-testid={testId}>—</span>
  return <KpiCard label={label} figure={figure} qualifier={t('overview.kpi.failed')} />
}

/** Sibling of {@link KpiFailed} for a read that is still in flight. */
function KpiLoading({ label, testId }: { label: string; testId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const figure = (
    <span aria-hidden data-testid={testId}>
      ···
    </span>
  )
  return <KpiCard label={label} figure={figure} qualifier={t('overview.kpi.loading')} />
}

/**
 * KPI card 1/3 (AC-1/AC-2) — requirements count + fit-gap split, from the
 * live `features/requirements` slice (PX-W7.2). Degrades independently: its
 * own loading treatment while the other cards render, its own failed state
 * on a 500 (never a page-level error, AC-11), zero derived from zero rows.
 *
 * Fix cycle 3 (GRP-F0A6E3C1), finding 2: a 403/404 on `openRequirements`
 * lands in `selectRequirementsBlocked`, never `selectRequirementsError`
 * (mirrors the board slice's error/blocked split) — reading `error` alone
 * left a refused list falling through to a fabricated zero count.
 *
 * Deferred findings c1-5/c2-1: `error` also carries a ROW-scoped mutation
 * refusal (e.g. a failed confirm/reject/supersede, `scope` set to the
 * requirement's `ref`) — reading `error !== null` alone turned a valid,
 * already-loaded count into "—" the moment any single row's verb failed.
 * Only an UNSCOPED error (`error.scope === undefined`) is the primary
 * list-read failure this card's own failed state is for.
 */
function RequirementsKpiCard() {
  const { t } = useTranslation('projectWorkspace')
  const loading = useAppSelector(selectRequirementsLoading)
  const error = useAppSelector(selectRequirementsError)
  const blocked = useAppSelector(selectRequirementsBlocked)
  const summary = useAppSelector(selectFitGapSummary)
  const listFailed = (error !== null && error.scope === undefined) || blocked !== null

  if (listFailed) {
    return <KpiFailed label={t('overview.kpi.requirements.label')} testId="kpi-requirements-failed" />
  }
  if (loading) {
    return <KpiLoading label={t('overview.kpi.requirements.label')} testId="kpi-requirements-loading" />
  }
  return (
    <KpiCard
      label={t('overview.kpi.requirements.label')}
      figure={summary.total}
      qualifier={t('overview.kpi.requirements.qualifier', {
        confirmed: summary.confirmed,
        proposed: summary.proposed,
      })}
    />
  )
}

/**
 * KPI card 2/3 (AC-1/AC-2) — build progress, from the live `features/board`
 * slice (PX-W7.4). Fix cycle 2 (wave 2, GRP-E51B8F73), finding 1: this card
 * must read `selectBoardListError` (`BuildQueue`'s own primary-list-read
 * channel), not `selectBoardError` — since 7.4's fix cycle 1, `openBoard`'s
 * rejection writes `listError` (or `blocked` for 403/404) and leaves `error`
 * untouched, so a non-403/404 failure on `GET tasks` never populated `error`
 * and this card's failed branch was dead, always falling through to a
 * fabricated "0/0 · 0% done".
 *
 * Fix cycle 3 (GRP-F0A6E3C1), finding 1: `selectBoardListError` alone still
 * missed a 403/404 on the same `openBoard` read — that rejection lands in
 * `selectBoardBlocked` (see `boardSlice`'s `openBoard.rejected`), so this
 * card also reads `blocked`, the same pair `BuildQueue` already reads.
 */
function BuildKpiCard() {
  const { t } = useTranslation('projectWorkspace')
  const loading = useAppSelector(selectBoardLoading)
  const error = useAppSelector(selectBoardListError)
  const blocked = useAppSelector(selectBoardBlocked)
  const progress = useAppSelector(selectBoardProgress)

  if (error !== null || blocked !== null) {
    return <KpiFailed label={t('overview.kpi.build.label')} testId="kpi-build-failed" />
  }
  if (loading) {
    return <KpiLoading label={t('overview.kpi.build.label')} testId="kpi-build-loading" />
  }
  // Fix wave 1 (GRP-B3D9F416), finding 1: the figure (`done/total`, a count
  // of weight-1 statuses) and the qualifier's percentage MUST name the same
  // metric — `progress.ratio` is a DIFFERENT, weighted partial-credit sum
  // (`selectBoardProgress`'s `PROGRESS_WEIGHTS`) that disagreed with the
  // figure whenever any task sat at a non-terminal, non-zero-weight status
  // (e.g. a six-task fixture with 1 done read "1/6 · 59% complete" — 59%
  // being the weighted sum, not the figure's own ratio). `pct` is derived
  // from the SAME `done`/`total` the figure renders.
  const pct = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100)
  return (
    <KpiCard
      label={t('overview.kpi.build.label')}
      figure={`${progress.done}/${progress.total}`}
      qualifier={t('overview.kpi.build.qualifier', { pct })}
    />
  )
}

/**
 * KPI card 3/3 (AC-1/AC-2) — budget, from the already-live `features/budgets`
 * slice. `activeMissing` (the 404 that MEANS "no budget agreed") renders that
 * fact — never `0%`, never a fabricated figure.
 */
function BudgetKpiCard() {
  const { t } = useTranslation('projectWorkspace')
  const loading = useAppSelector(selectBudgetsLoading)
  const error = useAppSelector(selectBudgetsError)
  const missing = useAppSelector(selectActiveBudgetMissing)
  const view = useAppSelector(selectBudgetVsActual)

  if (error !== null) {
    return <KpiFailed label={t('overview.kpi.budget.label')} testId="kpi-budget-failed" />
  }
  if (loading) {
    return <KpiLoading label={t('overview.kpi.budget.label')} testId="kpi-budget-loading" />
  }
  if (missing || view === null || !view.hasBudget) {
    return (
      <KpiCard
        label={t('overview.kpi.budget.label')}
        figure={<span data-testid="kpi-budget-missing">—</span>}
        qualifier={t('overview.kpi.budget.noneAgreed')}
      />
    )
  }
  const qualifier =
    view.actualCostMinorTotal !== undefined && view.budgetAmountMinor !== undefined
      ? t('overview.kpi.budget.consumedOf', {
          used: minorToK(view.actualCostMinorTotal, view.currency ?? 'EUR'),
          total: minorToK(view.budgetAmountMinor, view.currency ?? 'EUR'),
        })
      : t('overview.kpi.budget.revision', { revision: view.budgetRevision ?? '—' })
  return (
    <KpiCard
      label={t('overview.kpi.budget.label')}
      figure={view.consumedPct !== undefined ? `${view.consumedPct}%` : '—'}
      qualifier={qualifier}
    />
  )
}

/**
 * The needs-a-human table (AC-3) — this project's tasks in the server's own
 * human-gated statuses (`selectTasksAwaitingHuman`, `features/board`). The
 * owner is resolved through the live members projection first (the roster
 * this workspace already trusts), falling back to the task's own embedded
 * assignee/reviewer snapshot only when no member row matches; every owner
 * cell also carries `data-owner-source` (`"reviewer" | "assignee"`) and a
 * small qualifier naming which field resolved it — the column shows a
 * REVIEWER-FIRST inference, never an authoritative "owner" (deferred finding
 * c1-6; no precedence change). "Mine" is decided by
 * {@link selectSessionPrincipalKey}, never `user.id` alone (INV-6.6). The
 * table issues NO request of its own.
 *
 * Four branches, in order:
 *  - FAILED — the board's primary read failed or was refused. Fix cycle 3
 *    (GRP-F0A6E3C1), finding 3: `selectTasksAwaitingHuman` derives from the
 *    SAME `openBoard` read `BuildKpiCard` reads — a failed/blocked read
 *    still returns zero rows from that selector, so this branch runs BEFORE
 *    the emptiness check, off the same `selectBoardListError` /
 *    `selectBoardBlocked` pair `BuildKpiCard` and `BuildQueue` already read,
 *    so a 500/403 never falls through to the empty state.
 *  - LOADING (deferred finding c4-1) — the read is still in flight with no
 *    human-gated rows loaded yet (`selectBoardLoading && tasks.length === 0`)
 *    — the shared `LoadingPanel`, never the empty state.
 *  - EMPTY — the read succeeded with zero human-gated rows.
 *  - TABLE — the loaded rows.
 */
function NeedsAHumanTable({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const tasks = useAppSelector(selectTasksAwaitingHuman)
  const listError = useAppSelector(selectBoardListError)
  const blocked = useAppSelector(selectBoardBlocked)
  const boardLoading = useAppSelector(selectBoardLoading)
  const members = useAppSelector(selectProjectMembers)
  const principalKey = useAppSelector(selectSessionPrincipalKey)

  const ownerNameFor = (task: BoardTaskRow): string => {
    const ownerId = task.reviewerId ?? task.assigneeId
    if (ownerId === undefined) return t('overview.humanTable.unassigned')
    const member = members.find((m) => m.userId === ownerId)
    return member?.name ?? task.reviewer?.name ?? task.assignee?.name ?? ownerId
  }

  /** Which field resolved {@link ownerNameFor} — reviewer-first, never assignee-first (no precedence change, c1-6). */
  const ownerSourceFor = (task: BoardTaskRow): 'reviewer' | 'assignee' | null =>
    task.reviewerId !== undefined ? 'reviewer' : task.assigneeId !== undefined ? 'assignee' : null

  const readFailed = listError !== null || blocked !== null

  return (
    <Panel title={t('overview.humanTable.panelTitle')} className="lg:col-span-2">
      {readFailed ? (
        <div
          role="alert"
          data-testid="needs-human-failed"
          data-triad="error"
          className="rounded-lg border border-border bg-card px-4 py-5"
        >
          <SectionLabel>{t('overview.needsHuman.failedTitle')}</SectionLabel>
          <p className="mt-1.5 text-sm text-foreground">{t('overview.needsHuman.failedBody')}</p>
        </div>
      ) : boardLoading && tasks.length === 0 ? (
        <LoadingPanel label={t('overview.humanTable.loading')} />
      ) : tasks.length === 0 ? (
        <EmptyPanel
          title={t('overview.humanTable.emptyTitle')}
          body={t('overview.humanTable.emptyBody')}
        />
      ) : (
        <table className="w-full text-sm" data-testid="needs-a-human-table">
          <thead>
            <tr className="v-row">
              <th
                scope="col"
                className="py-1.5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
              >
                {t('overview.humanTable.columns.item')}
              </th>
              <th
                scope="col"
                className="py-1.5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
              >
                {t('overview.humanTable.columns.status')}
              </th>
              <th
                scope="col"
                className="py-1.5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
              >
                {t('overview.humanTable.columns.owner')}
              </th>
              <th
                scope="col"
                className="py-1.5 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
              >
                {t('overview.humanTable.columns.updated')}
              </th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => {
              const isMine =
                principalKey !== null &&
                (task.assigneeId === principalKey || task.reviewerId === principalKey)
              const ownerSource = ownerSourceFor(task)
              return (
                <tr key={task.legacyId} className="v-row" data-testid={`human-task-${task.legacyId}`}>
                  <td className="py-2.5 pr-3">
                    <Link to={`/project/${projectId}/build`} className="hover:underline">
                      {task.title}
                    </Link>
                    {isMine && (
                      <span data-testid="mine-badge">
                        <StatusPill tone="decided" className="ml-2">
                          {t('overview.humanTable.mine')}
                        </StatusPill>
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3">
                    <StatusPill tone="review">{t(`board.status.${task.status}`)}</StatusPill>
                  </td>
                  <td className="py-2.5 pr-3" data-owner-source={ownerSource ?? undefined}>
                    {ownerNameFor(task)}
                    {ownerSource !== null && (
                      <span className="ml-1 text-[11px] text-muted-foreground">
                        {t(`overview.humanTable.ownerSource.${ownerSource}`)}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap text-muted-foreground">
                    {epochLabel(task.updatedAt)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </Panel>
  )
}

export default function ProjectOverviewPage() {
  const { t } = useTranslation('projectWorkspace')
  const { projectId } = useParams()
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)

  const project = useAppSelector(selectProjectDetail)
  const blocked = useAppSelector(selectProjectBlocked)
  const loading = useAppSelector(selectProjectDetailLoading)
  const detailError = useAppSelector(selectProjectDetailError)
  const phase = useAppSelector(selectPhaseView)
  const gatesNonce = useAppSelector(selectGatesNonce)
  const boardNonce = useAppSelector(selectBoardNonce)
  const requirementsNonce = useAppSelector(selectRequirementsNonce)

  // Leave the workspace — UNMOUNT ONLY (AC-3). Deferred finding c1-4: the
  // board and requirements slices are NOT this page's to reset — they clear
  // their own rows on a project switch (`openBoard.pending` /
  // `openRequirements.pending`), and `BuildQueue`/`RequirementsRegisterPage`
  // own their own unmount resets. Keyed on identity only (`orgId`/
  // `projectId`), never on the two nonces below, so a realtime frame never
  // re-runs this cleanup (the BuildQueue precedent, GRP-B04710BD).
  useEffect(() => {
    return () => {
      dispatch(closeProject())
      dispatch(gatesClosed())
    }
  }, [dispatch, orgId, projectId])

  // Mount + refetch-on-invalidate (AC-1, AC-5's feed mount rides the panel).
  // `openProject` / `openGates` / the two budget thunks are ALL
  // shouldFetch-guarded by their own slice's OWN cache key — a nonce bump
  // re-dispatches them here too, but their condition no-ops since neither
  // nonce touches their cache. `openRequirements` / `openBoard` are the same
  // guard, EXCEPT the realtime action that bumps `boardNonce`/
  // `requirementsNonce` also calls that slice's own `markServerChanged()`
  // (TASK-3D6DB8B6, batch contract C-3) — so a board/requirements frame for
  // this project turns into exactly ONE reload of that slice's list, with no
  // reset of the project detail (deferred finding c1-1); a frame landing while
  // that list is loading is followed up once it settles (C-4).
  const loadWorkspace = useCallback(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(openProject({ orgId, projectId }))
    void dispatch(openGates({ orgId, projectId }))
    void dispatch(openRequirements({ orgId, projectId }))
    void dispatch(openBoard({ orgId, projectId }))
    void dispatch(fetchActiveBudget(projectId))
    void dispatch(fetchBudgetVsActual(projectId))
  }, [dispatch, orgId, projectId])

  // The mount load — immediate, keyed on identity only.
  useEffect(() => {
    loadWorkspace()
  }, [loadWorkspace])

  // The realtime reload, coalesced (TASK-41CE1DE2 fix cycle 1): the server
  // publishes two `task.*` frames per status change, and each used to re-run
  // the whole load above — two board reads (six GETs) for one change. The
  // shared hook folds a burst of board/requirements nonce bumps into ONE
  // reload after it goes quiet (the Build board's mechanism, not a copy).
  useCoalescedRealtimeReload(`${boardNonce}:${requirementsNonce}`, loadWorkspace)

  // Bus `project.updated` for THIS project bumps the gates nonce — refetch
  // the gate views (criteria evidence moves with roster writes).
  useEffect(() => {
    if (orgId === null || projectId === undefined || gatesNonce === 0) return
    void dispatch(openGates({ orgId, projectId }))
  }, [dispatch, orgId, projectId, gatesNonce])

  /*
   * The project rail follows the ROUTE and the LIVE row — never the demo hero.
   *
   * ⚠ This page used to pass `heroProject()` here, so every real engagement's
   * workspace carried the FIXTURE's client as its persistent left-rail heading
   * and every rail link pointed at the fixture's project id, while the H1 above
   * showed the caller's actual project. The manual pass caught it on a fresh
   * user with a real organisation; two review cycles did not, because nothing
   * asserted the rail. `ProjectBudgetPage` and `AgentSpendPage` already carried
   * this exact fix — the page every user lands on FIRST was the one that missed
   * it. `projectOverviewRail` lives in its own sibling module (ProjectOverviewRail.ts)
   * so a test can pin the fallback chain without rendering the page.
   *
   * PX-W7.6: the roster moving live is exactly the change memory
   * `px-hybrid-route-chrome-leaks-the-fixture` warns about — every chrome
   * prop below is still built from the route, i18n keys or this live row
   * only; none of them touch `@/demo/data` or `@/demo/scoping` (INV-6.3).
   */
  const rail = projectOverviewRail(projectId, project)

  // REVIEW D1 — the ribbon renders ONLY for a known delivery-cut key. The
  // mapping is TOTAL at this boundary (`toRibbonPhase` → null for absent or
  // unknown keys): `PhaseRibbon`/`demo/architecture` are LOCKED furniture
  // that throws on an unknown phase, and casting '' through that lookup
  // unmounted the whole tree for pre-kickoff projects.
  const ribbonPhase = toRibbonPhase(phase?.currentPhase ?? project?.currentPhase ?? null)
  // The KICKOFF card shows only once the phase VIEW has loaded and says the
  // engagement has no phase yet (never on a load-flash, never on the row's
  // fallback value). `!= null` guards BOTH null (never loaded) and a runtime
  // undefined off a malformed payload.
  const noPhaseLoaded =
    phase != null && (phase.currentPhase === undefined || phase.currentPhase === null)

  const onRetryClicked = (): void => {
    dispatch(clearProjectDetailError())
    if (orgId !== null && projectId !== undefined) {
      void dispatch(openProject({ orgId, projectId }))
    }
  }

  let content: React.ReactNode
  if (blocked !== null) {
    // AC-7 — the keyed refusal, and nothing else.
    content = <ProjectBlockedState blocked={blocked} />
  } else if (project === null) {
    /*
     * PX-W6.6 (AC-2): the SHARED panels. The workspace used to hand-roll both
     * states, so a shell that looked right could drift from every other
     * surface's wait and failure. The mapped KEY travels to the panel, never
     * `detailError.message` — an untranslated EN sentence must not land in the
     * NL UI.
     */
    content =
      detailError !== null ? (
        <ErrorPanel
          className="mx-auto max-w-xl"
          error={asApiError(detailError)}
          onRetryClicked={onRetryClicked}
        />
      ) : (
        <LoadingPanel label={t('loading')} />
      )
  } else {
    // Deferred finding c1-7: the sub-header used to fall back to a bare `—`
    // segment when no contract type/vertical/office was set, and rendered
    // `· {date}` even when `goLiveDate` was absent (a "— NL · —" reader with
    // no facts at all). Each PRESENT fact — and the go-live date, when set —
    // is its OWN `data-testid="overview-fact"` segment; a `·` separator
    // renders only BETWEEN two present segments, never as a placeholder.
    const facts = [project.contractType, project.vertical, project.office].filter(
      (fact): fact is string => Boolean(fact)
    )
    const goLive = project.goLiveDate ? isoDayLabel(project.goLiveDate) : null

    // Deferred finding c1-8: the badged demo band's "view run" links carried
    // the DEMO FIXTURE's project id (`demo/content/projectOverview.ts`,
    // built off `heroProject()`) verbatim — every real engagement's run log
    // pointed at `/project/van-dijk/...`. Rebase the fixture route's own
    // suffix onto the CURRENT project; `AgentRunRow` and the demo content
    // module are untouched (both are LOCKED furniture for this ticket).
    const rebaseDemoHref = (href: string): string =>
      projectId === undefined ? href : href.replace(/^\/project\/[^/]+/, `/project/${projectId}`)

    content = (
      <div className="w-full" data-testid="project-workspace">
        <header>
          <h1 className="font-serif text-[26px] font-semibold leading-8">{project.name}</h1>
          <p className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {/*
             * Fix cycle 1 (GRP-82297E9A): no health signal renders NO pill —
             * never a bare "—" placeholder chip (same rule as the facts).
             */}
            {project.health ? (
              <span data-testid="overview-health">
                <StatusPill tone={healthTone(project.health)}>{project.health}</StatusPill>
              </span>
            ) : null}
            {facts.map((fact, index) => (
              <span key={`fact-${index}`} data-testid="overview-fact">
                {index > 0 && <span aria-hidden>· </span>}
                {fact}
              </span>
            ))}
            {goLive !== null && (
              <span data-testid="overview-fact">
                {facts.length > 0 && <span aria-hidden>· </span>}
                {goLive}
              </span>
            )}
          </p>
        </header>

        {noPhaseLoaded && orgId !== null && projectId !== undefined ? (
          <KickoffPanel orgId={orgId} projectId={projectId} />
        ) : ribbonPhase !== null ? (
          <PhaseRibbon current={ribbonPhase} className="mt-5" />
        ) : null}

        <div className="mt-5 space-y-4">
          {!noPhaseLoaded && <PhasePanel />}

          {/* ── The live spine grows here (PX-W7.6): KPI row, needs-a-human, roster ── */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="overview-kpi-row">
            <RequirementsKpiCard />
            <BuildKpiCard />
            <BudgetKpiCard />
            <KpiCard
              label={t('overview.kpi.goLive.label')}
              figure={isoDayLabel(project.goLiveDate)}
              qualifier={t('overview.kpi.goLive.qualifier')}
            />
          </div>

          {projectId !== undefined && (
            <div className="grid gap-4 lg:grid-cols-3">
              <NeedsAHumanTable projectId={projectId} />
            </div>
          )}

          {orgId !== null && projectId !== undefined && (
            <ProjectMembersPanel orgId={orgId} projectId={projectId} />
          )}

          {orgId !== null && projectId !== undefined && (
            <GatesPanel orgId={orgId} projectId={projectId} />
          )}
          {orgId !== null && projectId !== undefined && (
            <VersionsPanel orgId={orgId} projectId={projectId} />
          )}
          {orgId !== null && projectId !== undefined && (
            <ProjectDocumentsPanel orgId={orgId} projectId={projectId} />
          )}
          {orgId !== null && projectId !== undefined && (
            <ProjectActivityFeed orgId={orgId} projectId={projectId} />
          )}
        </div>

        {/* ── Concept band: the overnight-runs panel is the ONLY thing left (AC-6) ── */}
        <section className="mt-8" aria-label={t('demoBand.title')} data-testid="demo-band">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <ConceptBadge />
            <p className="max-w-2xl text-xs text-muted-foreground">{t('demoBand.note')}</p>
          </div>

          <Panel title={AGENT_PANEL_TITLE}>
            <div>
              {OVERNIGHT_RUNS.map((run) => (
                <div
                  key={run.id}
                  className={cn(
                    run.tone === 'blocked' &&
                      'rounded-sm border-l-[3px] border-l-rose-ink bg-blocked-bg pl-2.5 font-medium',
                    run.tone === 'review' && 'rounded-sm border-l-2 border-l-review bg-review-bg/60 pl-2.5'
                  )}
                >
                  <AgentRunRow
                    tone={run.tone}
                    label={run.label}
                    time={run.time}
                    runHref={rebaseDemoHref(run.runHref)}
                    action={
                      <span className="hidden shrink-0 text-xs text-muted-foreground xl:inline">
                        {agentById(run.agentId).name}
                      </span>
                    }
                    text={run.text}
                  />
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {RUN_LOG_FOOTNOTE}{' '}
              <Link
                to={rebaseDemoHref(RUN_LOG_LINK.to)}
                className="inline-flex items-center gap-0.5 font-medium text-accent-foreground hover:underline"
              >
                {RUN_LOG_LINK.label}
                <ArrowRight className="h-3 w-3" aria-hidden />
              </Link>
            </p>
          </Panel>
        </section>

        <DensityNote>{t('overview.densityNote')}</DensityNote>
      </div>
    )
  }

  return (
    <AppShell
      moduleLabel={t('overview.moduleLabel')}
      breadcrumb={project?.name ?? (loading ? t('loading') : '—')}
      contextPill={(project?.office ?? t('contextPill')).toUpperCase()}
      nav={projectNav(rail)}
    >
      {content}
    </AppShell>
  )
}
